import React from 'react';
import { MultiSelect } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useControlDisabled } from './ControlDisabledContext';
import { MultiselectFieldProvider } from './MultiselectFieldContext';

export interface OASISMultiselectRenderProps {
  values: string[];
  onChange: (v: string[]) => void;
  onToggle: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}

export interface OASISMultiselectProps {
  qid: string;
  hideLabel?: boolean;
  /** Outer `[data-qid]` wrapper — layout/grid placement (`min-w-0`, spans, …). */
  className?: string;
  /** Wraps prompt + checklist (default checklist `MultiSelect` or compound subtree). */
  innerClassName?: string;
  /**
   * Design-system checklist only (`compoundContent` omitted). Passed to `MultiSelect`.
   * Put grid/columns/gaps on layout nodes wrapping compound `children`, not here.
   */
  checklistClassName?: string;
  checklistStyle?: React.CSSProperties;
  children?: (props: OASISMultiselectRenderProps) => React.ReactNode;
  /** Compound bespoke layout: subtree provides its own wrappers + `repeat` / `OASISMultiselectOption`. */
  compoundContent?: React.ReactNode;
}

const LABEL_CLASS = 'block text-sm font-semibold text-foreground';

export function OASISMultiselect({
  qid,
  className,
  innerClassName,
  checklistClassName,
  checklistStyle,
  hideLabel,
  children,
  compoundContent,
}: OASISMultiselectProps) {
  const inheritedDisabled = useControlDisabled();
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);

  const values = (answers[qid] as string[]) ?? [];
  const setValues = (v: string[]) => onAnswerChange(qid, v);
  const onChange = setValues;
  const onToggle = (v: string) => {
    const next = values.includes(v)
      ? values.filter(x => x !== v)
      : [...values, v];
    onAnswerChange(qid, next);
  };

  const options = (question?.values ?? []).map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));

  const label = question?.question ?? question?.description ?? qid;

  const renderProps: OASISMultiselectRenderProps = {
    values,
    onChange,
    onToggle,
    options,
    label,
  };

  const compound = compoundContent != null;

  const fieldCtx = {
    qid,
    values,
    toggle: onToggle,
    setValues,
    disabled: inheritedDisabled,
  };

  const inner = innerClassName ?? 'space-y-3';

  return (
    <div data-qid={qid} className={className}>
      {children ? (
        children(renderProps)
      ) : compound ? (
        <div className={inner}>
          {!hideLabel && <label className={LABEL_CLASS}>{label}</label>}
          <MultiselectFieldProvider value={fieldCtx}>
            {compoundContent}
          </MultiselectFieldProvider>
        </div>
      ) : (
        <div className={inner}>
          {!hideLabel && <label className={LABEL_CLASS}>{label}</label>}
          {options.length === 0 ? (
            <p className='text-muted-foreground text-xs leading-snug'>
              No checklist options in loaded schema for{' '}
              <code className='rounded bg-muted px-1 py-0.5 text-[11px] font-mono'>
                {qid}
              </code>
              . Select a RogerDoc schema + section that define this multiselect.
            </p>
          ) : (
            <MultiSelect
              disabled={inheritedDisabled}
              className={checklistClassName}
              style={checklistStyle}
              options={options}
              value={values}
              onChange={onChange}
            />
          )}
        </div>
      )}
    </div>
  );
}
